<template>
  <div v-if="show" 
       class="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-sm animate-in fade-in duration-300"
       role="dialog"
       aria-modal="true"
       aria-labelledby="valuation-modal-title"
       @keydown.esc="$emit('close')"
  >
    
    <Card class="w-full max-w-md shadow-2xl border border-border animate-in zoom-in-95 self-center bg-card overflow-hidden">
      <CardHeader class="border-b border-border pb-6 relative">
        <div class="flex items-center justify-between">
          <div class="space-y-1">
            <CardTitle id="valuation-modal-title" class="text-xl font-black tracking-tight text-foreground">Vérification de Valeur</CardTitle>
            <CardDescription class="font-medium italic text-muted-foreground text-xs">Ajustez la valorisation actuelle de l'actif.</CardDescription>
          </div>
          <Button variant="ghost" size="icon" class="h-8 w-8 text-muted-foreground hover:text-foreground" @click="$emit('close')" aria-label="Fermer la modale">
            <X class="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent class="py-6 space-y-6">
        <div v-if="investment" class="p-4 bg-muted/30 rounded-2xl border border-border space-y-2">
          <div class="flex items-center gap-3">
             <div class="w-10 h-10 bg-zinc-900 border border-zinc-800 rounded-xl flex items-center justify-center text-white shrink-0 shadow-lg shadow-black/20">
               <TrendingUp class="w-5 h-5" />
             </div>
             <div class="flex flex-col">
               <span class="text-sm font-black text-foreground leading-none mb-1">{{ investment.name }}</span>
               <span class="text-[10px] text-muted-foreground font-bold uppercase tracking-widest leading-none mt-1 whitespace-nowrap overflow-hidden text-ellipsis">Actuellement : {{ formatAmount(investment.currentValue) }}€</span>
             </div>
          </div>
          <p v-if="investment.url" class="pt-2 border-t border-zinc-100 flex justify-end">
            <a :href="investment.url" target="_blank" rel="noopener" class="text-[9px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 antialiased transition-colors">
              Source externe <ExternalLink class="w-3 h-3" />
            </a>
          </p>
        </div>

        <div class="space-y-4">
          <div class="space-y-2 px-1">
            <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Nouvelle Valorisation (€)</label>
            <input type="number" step="0.01" v-model="form.newValuation" placeholder="0.00" ref="valuationInput"
                   class="w-full bg-muted/30 border border-border rounded-2xl p-4 text-2xl font-black tabular-nums focus:ring-4 focus:ring-primary/10 outline-none transition-all shadow-inner text-foreground" />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="space-y-1.5">
              <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Date du relevé</label>
              <input type="date" v-model="form.valuationDate" 
                     class="w-full bg-muted/30 border border-border rounded-xl p-2.5 text-xs font-bold focus:ring-2 focus:ring-primary/10 outline-none transition-all shadow-sm text-foreground" />
            </div>
            <div class="space-y-1.5">
              <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Catégorie</label>
              <select v-model="form.valuationCategoryId" 
                      class="w-full bg-muted/30 border border-border rounded-xl p-2.5 text-xs font-bold focus:ring-2 focus:ring-primary/10 outline-none transition-all shadow-sm text-foreground">
                <option :value="null" class="bg-card">-- Sélectionner --</option>
                <option v-for="cat in movementCategories" :key="cat.id" :value="cat.id" class="bg-card">{{ cat.displayName }}</option>
              </select>
            </div>
          </div>
        </div>
      </CardContent>

      <CardFooter class="flex justify-end gap-3 bg-muted/30 p-6 border-t border-border">
        <Button variant="ghost" class="font-bold text-muted-foreground hover:bg-muted hover:text-foreground transition-all" @click="$emit('close')">Annuler</Button>
        <Button variant="finance" :disabled="!form.newValuation || saving" class="min-w-[180px] font-black uppercase tracking-widest h-12 text-xs shadow-xl shadow-black/20 active:scale-95 transition-all" @click="save">
          <RefreshCw v-if="saving" class="w-4 h-4 mr-2 animate-spin" />
          <Save v-else class="w-4 h-4 mr-2" />
          {{ saving ? 'Mise à jour...' : 'Actualiser Valeur' }}
        </Button>
      </CardFooter>
    </Card>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { 
  X, TrendingUp, ExternalLink, 
  RefreshCw, Save 
} from 'lucide-vue-next'

// Utilities
import { formatAmount } from '@/lib/utils'
import { useGlobalState } from '@/composables/useGlobalState'

// UI Components
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui/card'

const props = defineProps({
  show: Boolean,
  investment: Object,
  movementCategories: Array,
  rawMovementCategories: Array
})

const emit = defineEmits(['close', 'saved'])

const form = ref({
  newValuation: '',
  valuationDate: new Date().toISOString().split('T')[0],
  valuationCategoryId: null
})
const valuationInput = ref(null)
const saving = ref(false)

const { getAuthHeader } = useGlobalState()

watch(() => props.show, (newVal) => {
  if (newVal && props.investment) {
    form.value.newValuation = props.investment.currentValue
    form.value.valuationDate = new Date().toISOString().split('T')[0]
    
    // Auto-select "Valorisation"
    const valCat = props.rawMovementCategories.find(c => c.name.toLowerCase() === 'valorisation')
    if (valCat) form.value.valuationCategoryId = valCat.id
    else form.value.valuationCategoryId = null

    nextTick(() => {
        if (valuationInput.value) valuationInput.value.focus()
    })
  }
})

const save = async () => {
  if (form.value.newValuation === null || form.value.newValuation === '') return
  
  saving.value = true
  try {
    const res = await fetch(`/api/investments/${props.investment.id}/valuation`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            newValuation: form.value.newValuation.toString(),
            date: form.value.valuationDate,
            categoryId: form.value.valuationCategoryId
        })
    })

    if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.error || "Erreur lors de l'enregistrement.")
    }

    emit('saved')
  } catch (e) {
    alert(e.message || "Erreur lors de l'enregistrement.")
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.tabular-nums {
  font-variant-numeric: tabular-nums;
}
</style>
